import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ShieldCheck, Key, ArrowLeft } from 'lucide-react';

// 3D rotating Adela logo, shared by the password screen and the 2FA screen.
// Many tightly-spaced copies along Z with a single front-to-back brightness
// ramp read as ONE solid, shaded logo when it turns edge-on (the old version
// used only 9 copies at 1.5px spacing with both faces bright, which looked
// like a few separate sheets). phase drives the success/fail/fly animations.
const LOGO_LAYERS = 24;   // copies stacked through ~12px of depth
const LOGO_DEPTH = 12;    // total extrusion depth in px

const LoginLogoStage = ({ stageRef, phase, flyStyle }) => (
  <div
    ref={stageRef}
    className={`login-logo-stage mx-auto ${phase === 'denied' ? 'login-logo-denied' : ''} ${
      phase === 'success' ? 'login-logo-success' : ''
    } ${phase === 'fly' ? 'login-logo-fly' : ''}`}
    style={flyStyle || undefined}
  >
    <div
      className={`login-logo-spinner ${
        phase === 'accepted' || phase === 'fly' ? 'login-logo-spin-fast' : ''
      }`}
    >
      {Array.from({ length: LOGO_LAYERS }, (_, i) => {
        const t = i / (LOGO_LAYERS - 1);            // 0 = back face, 1 = front face
        const z = (t - 0.5) * LOGO_DEPTH;            // -6px .. +6px
        const isFace = i === 0 || i === LOGO_LAYERS - 1;  // the two crisp logo faces
        // SYMMETRIC brightness: both faces full colour, the extruded middle
        // darker. An asymmetric (bright front / dark back) ramp made the logo
        // pop dark the instant it crossed edge-on — that was the "blip".
        const brightness = 0.45 + 0.55 * Math.abs(t - 0.5) * 2;
        return (
          <img
            key={i}
            src="/adela-logo.png"
            alt={i === LOGO_LAYERS - 1 ? 'Adela Manufacturing Logo' : ''}
            aria-hidden={i !== LOGO_LAYERS - 1}
            className="login-logo-layer"
            style={{
              transform: `translateZ(${z}px)`,
              filter: isFace ? 'none' : `brightness(${brightness.toFixed(3)})`,
            }}
          />
        );
      })}
    </div>
  </div>
);

const Login = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [requires2FA, setRequires2FA] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [totpCode, setTotpCode] = useState('');
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showForgotUsername, setShowForgotUsername] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  // Logo animation: 'idle' (slow spin) | 'accepted' (fast spin) | 'fly'
  // (zoom to the sidebar logo position) | 'denied' (red pulse)
  const [logoPhase, setLogoPhase] = useState('idle');
  const [flyStyle, setFlyStyle] = useState(null);
  // WP-SS1: a toast disappears after a few seconds. When the account is locked
  // the person needs the message to stay on screen, so we keep it here too.
  const [lockNotice, setLockNotice] = useState('');
  // WP-2E: a 2FA set-up lock (HTTP 423) always needs the "ask an admin" line,
  // whatever wording the server used.
  const [lockNoticeAskAdmin, setLockNoticeAskAdmin] = useState(false);
  const logoStageRef = useRef(null);

  const { login, verify2FA, verifyRecoveryCode, logoutNotice, setLogoutNotice } = useAuth();

  // WP-2E: when the app signed the person out on purpose (the 2FA set-up
  // reminder was cleared for the third time), the reason has to be waiting for
  // them here, not in a toast that has already faded.
  useEffect(() => {
    if (logoutNotice) {
      setLockNotice(logoutNotice);
      setLockNoticeAskAdmin(true);
    }
    // Deliberately mount-only: after this the notice belongs to the login
    // screen, which clears it as soon as the person tries again.
  }, []);   // eslint-disable-line

  const handleSubmit = async (e) => {
    e.preventDefault();
    // A fresh attempt clears the parked sign-out reason, so it cannot come
    // back on the next render.
    if (logoutNotice) setLogoutNotice('');
    if (!credentials.username || !credentials.password) {
      toast.error('Please enter both username and password');
      return;
    }

    setLoading(true);
    const result = await login(credentials, { deferComplete: true });

    if (!result.success && result.status === 423) {
      // WP-2E — the account is locked because the 2FA set-up reminder was
      // cleared three times. Only an admin can clear this one, so the message
      // stays on screen with the "ask an admin" line under it.
      setLockNotice(result.error || 'This account is locked.');
      setLockNoticeAskAdmin(true);
    } else if (!result.success && result.status === 429) {
      // Two different things answer with 429: the account lock (5 wrong
      // passwords, 15 minutes) and the per-computer rate limit (more than 5
      // login attempts in a minute). Only the account lock is something an
      // admin can clear, so only that one gets the "ask an admin" line.
      // A lock notice already on screen must survive: the rate limit trips on
      // the very next try, and "wait a minute" would otherwise replace "locked
      // for 15 minutes" and take away the only advice that helps.
      setLockNotice((prev) => {
        if (/lock/i.test(result.error || '')) return result.error;
        if (/lock/i.test(prev || '')) return prev;
        return 'Too many login attempts from this computer. Wait a minute, then try again.';
      });
    } else {
      // Anything that is not a 429 or a 423 (wrong password, success, 2FA)
      // clears it.
      setLockNotice('');
      setLockNoticeAskAdmin(false);
    }

    if (result.requires2FA) {
      setRequires2FA(true);
      setSessionToken(result.sessionToken);
      setLoading(false);
    } else if (result.success) {
      // Accepted: spin up fast, then fly the logo into the sidebar logo
      // position (top-left) before letting the app switch screens.
      setLogoPhase('accepted');
      setTimeout(() => {
        const stage = logoStageRef.current;
        if (!stage) {
          result.complete();
          return;
        }
        const rect = stage.getBoundingClientRect();
        // Sidebar logo in Layout.js: 40px image at left 16px, vertically
        // centred in a 64px header → centre point (36, 32).
        const dx = 36 - (rect.left + rect.width / 2);
        const dy = 32 - (rect.top + rect.height / 2);
        setFlyStyle({
          position: 'fixed',
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          margin: 0,
          zIndex: 60,
        });
        setLogoPhase('fly');
        // Two frames so the fixed position paints before the transform
        // transition starts.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            setFlyStyle((s) => ({
              ...s,
              transform: `translate(${dx}px, ${dy}px) scale(${40 / rect.width})`,
            }));
          });
        });
        setTimeout(() => result.complete(), 850);
      }, 900);
    } else {
      // Denied: stop the spin, pulse red, then settle back to idle.
      setLoading(false);
      setLogoPhase('denied');
      setTimeout(() => setLogoPhase('idle'), 1500);
    }
  };

  // Shared red-pulse used for both a wrong password and a wrong 2FA code.
  const playDeniedPulse = () => {
    setLogoPhase('denied');
    setTimeout(() => setLogoPhase('idle'), 1500);
  };

  // 2FA success: green pulse, then fly the logo off to the TOP-RIGHT corner
  // (the password success flies to the top-LEFT sidebar logo instead).
  const playSuccessFlight = (complete) => {
    setLogoPhase('success');
    setTimeout(() => {
      const stage = logoStageRef.current;
      if (!stage) {
        complete();
        return;
      }
      const rect = stage.getBoundingClientRect();
      // Fly to the sidebar logo position (top-LEFT), the same target the
      // password success flow uses, so it lands on the app's sidebar logo.
      const targetX = 36;
      const targetY = 32;
      const dx = targetX - (rect.left + rect.width / 2);
      const dy = targetY - (rect.top + rect.height / 2);
      setFlyStyle({
        position: 'fixed',
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        margin: 0,
        zIndex: 60,
      });
      setLogoPhase('fly');
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setFlyStyle((s) => ({
            ...s,
            transform: `translate(${dx}px, ${dy}px) scale(${40 / rect.width})`,
          }));
        });
      });
      setTimeout(() => complete(), 850);
    }, 1300);   // let the green pulse (0.65s x2) finish first
  };

  // D-SEC12 (17/09/2026) — five wrong 2FA codes lock the account for 15
  // minutes, exactly like five wrong passwords. Two different things answer
  // with 429 on this screen: the account lock, and the per-computer rate limit
  // (D-SEC10). Both need to STAY on screen rather than fade as a toast, and
  // only the account lock is something an admin can clear.
  const noteCodeScreenRefusal = (result) => {
    if (result.status !== 429) {
      setLockNotice('');
      setLockNoticeAskAdmin(false);
      return;
    }
    setLockNotice((prev) => {
      // The account lock wins: it is the one that needs an admin, and the
      // per-computer limit trips on the very next try, so "wait a minute" must
      // never replace "locked for 15 minutes".
      if (/lock/i.test(result.error || '')) return result.error;
      if (/lock/i.test(prev || '')) return prev;
      return result.error
        || 'Too many attempts from this computer. Wait a minute, then try again.';
    });
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!totpCode || totpCode.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }

    setLoading(true);
    const result = await verify2FA(sessionToken, totpCode, { deferComplete: true });

    if (result.success) {
      setLockNotice('');
      playSuccessFlight(result.complete);
    } else {
      noteCodeScreenRefusal(result);
      setLoading(false);
      setTotpCode('');
      playDeniedPulse();
    }
  };

  const handleVerifyRecovery = async (e) => {
    e.preventDefault();
    if (!recoveryCode || recoveryCode.length < 8) {
      toast.error('Please enter your recovery code');
      return;
    }

    setLoading(true);
    const result = await verifyRecoveryCode(sessionToken, recoveryCode, { deferComplete: true });

    if (result.success) {
      setLockNotice('');
      playSuccessFlight(result.complete);
    } else {
      noteCodeScreenRefusal(result);
      setLoading(false);
      setRecoveryCode('');
      playDeniedPulse();
    }
  };

  const handleChange = (e) => {
    setCredentials({
      ...credentials,
      [e.target.name]: e.target.value
    });
  };

  const handleBackToLogin = () => {
    setRequires2FA(false);
    setSessionToken(null);
    setTotpCode('');
    setRecoveryCode('');
    setUseRecoveryCode(false);
    setCredentials({ username: '', password: '' });
  };

  // 2FA Verification Screen
  // The standing notice. A toast disappears after a few seconds; a locked
  // account needs the message to stay put. Shown on the password screen and —
  // since D-SEC12 gave wrong codes the same 15-minute lock — on the 2FA code
  // screen as well.
  const lockNoticeBanner = lockNotice ? (
    <div
      role="alert"
      className="rounded-lg border border-amber-500/50 bg-amber-500/10 px-4 py-3 text-sm text-amber-200"
    >
      <p className="font-semibold">{lockNotice}</p>
      {/* Only an account lock can be cleared by an admin. The per-computer
          rate limit just needs a minute. */}
      {lockNoticeAskAdmin ? (
        /* WP-2E: an account lock (2FA or 3 strikes) never clears on its own,
           so do not tell them to wait it out. Both messages that set this flag
           already say to ask an admin, so this line only adds the fact that
           waiting will not help. */
        <p className="mt-1 text-amber-200/80">
          This lock does not clear on its own. It stays until an admin clears it.
        </p>
      ) : (/lock/i.test(lockNotice) && (
        <p className="mt-1 text-amber-200/80">
          Ask an admin to unlock you in Staff &amp; Security if you can&apos;t wait.
        </p>
      ))}
    </div>
  ) : null;

  if (requires2FA) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto mb-4" style={{ height: '96px' }}>
              <LoginLogoStage stageRef={logoStageRef} phase={logoPhase} flyStyle={flyStyle} />
            </div>
            <div className={logoPhase === 'fly' ? 'login-ui-fade' : ''}>
              <h1 className="text-2xl font-bold text-white mb-2">Two-Factor Authentication</h1>
              <p className="text-gray-400">
                {useRecoveryCode
                  ? 'Enter one of your recovery codes'
                  : 'Enter the 6-digit code from your authenticator app'}
              </p>
            </div>
          </div>

          {lockNoticeBanner}

          {!useRecoveryCode ? (
            <form className={`mt-8 space-y-6 ${logoPhase === 'fly' ? 'login-ui-fade' : ''}`} onSubmit={handleVerify2FA}>
              <div>
                <label htmlFor="totpCode" className="block text-sm font-medium text-gray-300 mb-2">
                  Verification Code
                </label>
                <input
                  id="totpCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={6}
                  className="misty-input w-full text-center text-2xl tracking-widest font-mono"
                  placeholder="000000"
                  value={totpCode}
                  onChange={(e) => {
                    const newCode = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setTotpCode(newCode);
                  }}
                  onKeyDown={(e) => {
                    // Submit on Enter key when code is complete
                    if (e.key === 'Enter' && totpCode.length === 6 && !loading) {
                      e.preventDefault();
                      handleVerify2FA(e);
                    }
                  }}
                  disabled={loading}
                  autoFocus
                />
              </div>

              <button
                type="submit"
                disabled={loading || totpCode.length !== 6}
                className="w-full misty-button misty-button-primary py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mr-2"></div>
                    Verifying...
                  </div>
                ) : (
                  'Verify'
                )}
              </button>

              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={() => setUseRecoveryCode(true)}
                  className="text-sm text-yellow-400 hover:text-yellow-300 flex items-center justify-center gap-2 mx-auto"
                >
                  <Key className="h-4 w-4" />
                  Use a recovery code instead
                </button>
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="text-sm text-gray-400 hover:text-gray-300 flex items-center justify-center gap-2 mx-auto"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </button>
              </div>
            </form>
          ) : (
            <form className={`mt-8 space-y-6 ${logoPhase === 'fly' ? 'login-ui-fade' : ''}`} onSubmit={handleVerifyRecovery}>
              <div>
                <label htmlFor="recoveryCode" className="block text-sm font-medium text-gray-300 mb-2">
                  Recovery Code
                </label>
                <input
                  id="recoveryCode"
                  type="text"
                  className="misty-input w-full text-center text-xl tracking-widest font-mono uppercase"
                  placeholder="XXXXXXXX"
                  value={recoveryCode}
                  onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => {
                    // Submit on Enter key when recovery code is complete
                    if (e.key === 'Enter' && recoveryCode.length >= 8 && !loading) {
                      e.preventDefault();
                      handleVerifyRecovery(e);
                    }
                  }}
                  disabled={loading}
                  autoFocus
                />
                <p className="text-xs text-gray-500 mt-2">
                  Recovery codes are 8 characters long
                </p>
              </div>

              <button
                type="submit"
                disabled={loading || recoveryCode.length < 8}
                className="w-full misty-button misty-button-primary py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mr-2"></div>
                    Verifying...
                  </div>
                ) : (
                  'Verify Recovery Code'
                )}
              </button>

              <div className="text-center space-y-2">
                <button
                  type="button"
                  onClick={() => setUseRecoveryCode(false)}
                  className="text-sm text-yellow-400 hover:text-yellow-300 flex items-center justify-center gap-2 mx-auto"
                >
                  <ShieldCheck className="h-4 w-4" />
                  Use authenticator app instead
                </button>
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="text-sm text-gray-400 hover:text-gray-300 flex items-center justify-center gap-2 mx-auto"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to login
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Normal Login Screen
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          {/* Adela Manufacturing Logo — 3D slow-rotating with glow.
              The holder keeps the layout height when the stage goes
              position:fixed for the fly-to-sidebar animation. */}
          <div className="mx-auto mb-4" style={{ height: '96px' }}>
            <LoginLogoStage stageRef={logoStageRef} phase={logoPhase} flyStyle={flyStyle} />
          </div>
          <div className={logoPhase === 'fly' ? 'login-ui-fade' : ''}>
            <h1 className="text-3xl font-bold text-yellow-400 mb-2">Misty Manufacturing</h1>
            <h2 className="text-xl text-gray-300 mb-2">Management System</h2>
            <p className="text-sm text-gray-400">Production, Orders, Stock & Payroll</p>
          </div>
        </div>

        <form
          className={`mt-8 space-y-6 ${logoPhase === 'fly' ? 'login-ui-fade' : ''}`}
          onSubmit={handleSubmit}
        >
          <div className="space-y-4">
            {lockNoticeBanner}

            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-300 mb-1">
                Username
              </label>
              <input
                id="username"
                name="username"
                type="text"
                required
                className="misty-input w-full"
                placeholder="Enter your username"
                value={credentials.username}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-1">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="misty-input w-full"
                placeholder="Enter your password"
                value={credentials.password}
                onChange={handleChange}
                disabled={loading}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full misty-button misty-button-primary py-3 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="login-submit-button"
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-900 mr-2"></div>
                  Signing in...
                </div>
              ) : (
                'Sign in'
              )}
            </button>
          </div>

          <div className="flex items-center justify-center space-x-4 text-sm">
            <button
              type="button"
              onClick={() => setShowForgotPassword(true)}
              className="text-yellow-400 hover:text-yellow-300 transition-colors"
            >
              Forgot Password?
            </button>
            <span className="text-gray-600">|</span>
            <button
              type="button"
              onClick={() => setShowForgotUsername(true)}
              className="text-yellow-400 hover:text-yellow-300 transition-colors"
            >
              Forgot Username?
            </button>
          </div>
        </form>

        <div className={`text-center mt-4 ${logoPhase === 'fly' ? 'login-ui-fade' : ''}`}>
          <a
            href="/clock-in"
            className="inline-flex items-center text-sm text-gray-400 hover:text-yellow-400 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            Clock-In / Clock-Out Kiosk
          </a>
        </div>

        <div className={`text-center mt-3 ${logoPhase === 'fly' ? 'login-ui-fade' : ''}`}>
          <p className="text-xs text-gray-500">
            © 2025 Misty Manufacturing. All rights reserved.
          </p>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !window.getSelection().toString()) setShowForgotPassword(false); }}
        >
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-4">Reset Password</h3>
            <p className="text-gray-400 text-sm mb-4">
              Enter your email address and we'll send you instructions to reset your password.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
              <div className="bg-blue-900/30 border border-blue-600/30 rounded-lg p-3">
                <p className="text-blue-300 text-sm">
                  <strong>Note:</strong> Please contact your system administrator to reset your password. They can update your credentials in Staff & Security settings.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotEmail('');
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forgot Username Modal */}
      {showForgotUsername && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !window.getSelection().toString()) setShowForgotUsername(false); }}
        >
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-4">Forgot Username</h3>
            <p className="text-gray-400 text-sm mb-4">
              Enter your email address to retrieve your username.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={forgotEmail}
                  onChange={(e) => setForgotEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                />
              </div>
              <div className="bg-blue-900/30 border border-blue-600/30 rounded-lg p-3">
                <p className="text-blue-300 text-sm">
                  <strong>Note:</strong> Please contact your system administrator to retrieve your username. They can view user accounts in Staff & Security settings.
                </p>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotUsername(false);
                    setForgotEmail('');
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
